// Translation service using tdvorak.dev API and internal AI API

import { api } from './api';

export interface TranslationRequest {
  q: string;
  source: string;
  target: string;
  format?: 'text' | 'html';
  alternatives?: number;
}

export interface TranslationResponse {
  translatedText: string;
  alternatives?: string[];
}

// Internal AI translation interface
export interface AITranslationRequest {
  text: string;
  from?: string; // default 'cs'
  to?: string;   // default 'en'
}

export interface AITranslationResponse {
  translated_text: string;
  from: string;
  to: string;
}

const TRANSLATE_API_URL = 'https://translate.tdvorak.dev/translate';

// Cache translations to avoid repeated API calls
const translationCache = new Map<string, string>();

// Translate using internal AI API
export async function translateWithAI(
  text: string,
  sourceLang: string = 'cs',
  targetLang: string = 'en'
): Promise<string> {
  const cacheKey = `ai-${sourceLang}-${targetLang}-${text}`;
  
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey)!;
  }
  
  try {
    const response = await api.post<AITranslationResponse>('/ai/translate', {
      text,
      from: sourceLang,
      to: targetLang
    });
    
    const translatedText = response.data.translated_text;
    translationCache.set(cacheKey, translatedText);
    return translatedText;
  } catch (error) {
    console.error('AI translation failed:', error);
    return text; // Return original text if translation fails
  }
}

export async function translateText(
  text: string,
  sourceLang: string,
  targetLang: string,
  options: {
    format?: 'text' | 'html';
    alternatives?: number;
  } = {}
): Promise<TranslationResponse> {
  try {
    const response = await fetch(TRANSLATE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        q: text,
        source: sourceLang,
        target: targetLang,
        format: options.format || 'text',
        alternatives: options.alternatives || 0,
      }),
    });

    if (!response.ok) {
      throw new Error(`Translation API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Translation error:', error);
    throw error;
  }
}

// Convenience function for blog translation
export async function translateBlogContent(
  title: string,
  content: string,
  sourceLang: string,
  targetLang: string
): Promise<{ translatedTitle: string; translatedContent: string }> {
  try {
    const [titleResult, contentResult] = await Promise.all([
      translateText(title, sourceLang, targetLang, { format: 'text' }),
      translateText(content, sourceLang, targetLang, { format: 'html' }),
    ]);

    return {
      translatedTitle: titleResult.translatedText,
      translatedContent: contentResult.translatedText,
    };
  } catch (error) {
    console.error('Blog translation error:', error);
    throw error;
  }
}

// Function to detect language (simple heuristic)
export function detectLanguage(text: string): 'cs' | 'en' {
  // Simple Czech language detection based on common Czech characters and words
  const czechChars = /[áčďéěíňóřšťúůýž]/i;
  const czechWords = /\b(a|je|v|na|z|do|od|po|pro|při|pod|před|s|se|si|k|ke|t|ti|tom|tomu|tím|těm|těmu|toho|tomu|jeho|jehož|její|jejich|svůj|svá|své|svých|být|být|jsem|jsi|je|jsme|jste|jsou|mít|mít|mám|máš|má|náš|vaše|který|která|které|kterým|kterými|kterých|aby|aby|jako|jako|kdy|když|kde|kam|kudy|co|proč|jak|jak|kolik|kdo|kdy|jestli|li|jestliže|pokud|ačkoli|i když|proto|protože|tudíž|tedy|však|ale|avšak|nebo|buď|anebo|ani|když|pokud|jestli|li|jestliže|aby|aby|by|bych|bys|bychom|byste|by|by|by|by|by)\b/i;
  
  if (czechChars.test(text) || czechWords.test(text)) {
    return 'cs';
  }
  
  return 'en';
}

// Czech to English mappings for common UI text (fallback)
export const czechToEnglishMap: Record<string, string> = {
  'Komentáře': 'Comments',
  'Zatím žádné komentáře.': 'No comments yet.',
  'Napište komentář…': 'Write a comment...',
  'Respektujte prosím pravidla slušné diskuse.': 'Please respect the rules of decent discussion.',
  'Odeslat': 'Send',
  'Odpovědět': 'Reply',
  'Nahlásit': 'Report',
  'Upravit': 'Edit',
  'Smazat': 'Delete',
  'Uložit': 'Save',
  'Zrušit': 'Cancel',
  'Načítám…': 'Loading...',
  'Načíst další': 'Load more',
  'Označeno administrátorem': 'Marked by administrator',
  'Čeká na schválení': 'Awaiting approval',
  'Váš komentář čeká na schválení (automatická moderace).': 'Your comment is awaiting approval (automatic moderation).',
  'Nepodařilo se odeslat komentář': 'Failed to post comment',
  'Žádost o odblokování odeslána.': 'Unban request sent.',
  'Děkujeme za nahlášení. Moderátor se na komentář podívá.': 'Thank you for reporting. A moderator will review the comment.',
  'Pro přidání komentáře se prosím': 'To add a comment, please',
  'přihlaste': 'log in',
  'Odpověď na komentář': 'Reply to comment',
  'Zrušit odpověď': 'Cancel reply',
  'Váš účet je dočasně zablokován pro komentování. Můžete odeslat žádost o odblokování s krátkým vysvětlením.': 'Your account is temporarily blocked from commenting. You can send an unban request with a brief explanation.',
  'Vaše zpráva pro administrátory…': 'Your message for administrators...',
  'Odeslat žádost o odblokování': 'Send unban request',
  'Prosím o odblokování komentářů. Děkuji.': 'Please unblock my comments. Thank you.',
  'Palec nahoru': 'Thumbs up',
  'Srdíčko': 'Heart',
  'Úsměv': 'Smile',
  'Překvapení': 'Surprised',
  'Palec dolů': 'Thumbs down',
  '(upraveno)': '(edited)',
  'Admin': 'Admin',
  'Anonym': 'Anonymous',
  'Uživatel': 'User'
};

// Quick translation for common UI text using mapping
export const translateQuick = (czechText: string): string => {
  return czechToEnglishMap[czechText] || czechText;
};

// Translate comment content using AI with fallback to mapping
export const translateComment = async (czechText: string): Promise<string> => {
  // First try quick mapping for common phrases
  const quickTranslation = translateQuick(czechText);
  if (quickTranslation !== czechText) {
    return quickTranslation;
  }
  
  // For longer content, use AI translation
  if (czechText.length > 10) {
    return await translateWithAI(czechText, 'cs', 'en');
  }
  
  return czechText;
};
